import { DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Timeout } from "react-number-format/types/types";
import QRCode from "react-qr-code";
import VerificationInput from "react-verification-input";

import Button from "@/components/buttons/Button.tsx";
import { Icon } from "@/components/common/Icon";
import Spinner from "@/components/helpers/Spinner/Spinner";

import { USER_KEYS, useConfirmLoginCode, useCreateLoginCode } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

interface AddSessionModalForm {
  code: string;
}

const AddSessionModal = (): JSX.Element => {
  const { t } = useTranslation();
  const lang = useLang();

  const { control, handleSubmit, reset } = useForm<AddSessionModalForm>();

  const { data: res, isLoading } = useCreateLoginCode();
  const { mutate, isPending } = useConfirmLoginCode();
  const refreshTimeout = useRef<Timeout>();
  const queryClient = useQueryClient();

  const onSubmit = (data: AddSessionModalForm) => {
    mutate(data.code, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_KEYS.sessions(lang) });
        onSuccess(t("Sessions.confirm-code-success"));
      },
      onSettled: () => {
        reset({ code: "" });
      },
      onError,
    });
  };

  useEffect(() => {
    refreshTimeout.current = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.createCode(lang) });
    }, 60 * 1000);
    return () => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.createCode(lang) });
      clearInterval(refreshTimeout.current);
    };
  }, []);

  return (
    <div className="flex max-w-[32.5rem] flex-col items-center">
      <DialogTitle as={"h2"} className={"text-3xl/10 font-semibold"}>
        {t("Sessions.add-device")}
      </DialogTitle>
      <form className={"mt-10 flex flex-col items-center gap-[1.875rem] max-lg:mt-6"} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={"code"}
          control={control}
          rules={{ required: true, minLength: 6 }}
          render={({ field }) => (
            <VerificationInput
              placeholder={""}
              validChars={"0-9"}
              value={field.value}
              onChange={field.onChange}
              classNames={{
                container: "verification-input-container",
                character: "verification-input-character",
                characterSelected: "pin-input hover:bg-surface-bright verification-input-character-selected outline-0",
                characterFilled: "verification-input-character-filled",
              }}
            />
          )}
        />
        <Button
          color={"black"}
          size={"medium"}
          type="submit"
          isLoading={isPending}
          icon={<Icon name={"plus"} className={"size-6 stroke-on-primary"} />}
          className={"w-full px-8 py-[1.125rem]"}
        >
          {t("Sessions.add-device")}
        </Button>
      </form>
      <div className={"mt-10 flex w-full flex-col items-center max-lg:hidden"}>
        <h3 className={"mb-4 text-center text-base"}>{t("Sessions.scan-qr")}</h3>
        {isLoading ? (
          <div className="flex h-[18.75rem] w-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          res?.data.code && (
            <QRCode
              style={{ height: "auto", maxWidth: "18.75rem", width: "18.75rem" }}
              value={res?.data.code.toString()}
              viewBox={`0 0 256 256`}
              bgColor="black"
              fgColor="white"
            />
          )
        )}
      </div>
    </div>
  );
};

export default AddSessionModal;
